// El scroll de la conversación del Agente, la parte con DOM —
// ADR-2026-09-13-el-scroll-del-agente. Las reglas están en `scrollDeConversacion.ts`.
//
// Qué hace:
//   • Al montar (o al cambiar de chat), vuelve a donde el usuario había quedado en ESE
//     chat; si no hay recuerdo, al fondo. La posición se guarda en cada scroll.
//   • Mientras el agente responde, SIGUE el crecimiento del contenido (un ResizeObserver
//     sobre la columna: cada token, cada card que aparece) y baja al fondo. Instantáneo y
//     no suave: un smooth por token se ve como un tembleque, y además sus frames
//     intermedios se confundirían con scroll del usuario.
//   • Un gesto del usuario hacia arriba corta el seguimiento en el acto; se retoma solo
//     cuando vuelve a llegar al fondo, o al tocar el botón de "ir al final".
//   • Expone si está lejos del fondo (para el botón) y `irAlFondo` (suave).

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import {
  estaCercaDelFondo,
  gestoHaciaArriba,
  guardarPosicion,
  posicionInicial,
  UMBRAL_CERCA_PX,
  UMBRAL_REENGANCHE_PX,
} from './scrollDeConversacion';

export function useScrollDeConversacion(args: {
  scrollRef: RefObject<HTMLDivElement | null>;
  /** Con qué chat se recuerda la posición (el id del chat, o un marcador si aún no tiene). */
  clave: string;
  /** Hay conversación montada (con el chat vacío no hay nada que scrollear). */
  activo: boolean;
}): {
  lejosDelFondo: boolean;
  irAlFondo: () => void;
  /** Al mandar un mensaje: vuelve a seguir y baja al fondo (el usuario quiere ver lo suyo). */
  alEnviar: () => void;
} {
  const { scrollRef, clave, activo } = args;
  const siguiendoRef = useRef(true);
  const [lejosDelFondo, setLejosDelFondo] = useState(false);

  // Dónde arrancar. Layout effect: antes del primer pintado, así no se ve el salto.
  useLayoutEffect(() => {
    const sc = scrollRef.current;
    if (!sc || !activo) return;
    sc.scrollTop = posicionInicial(clave, sc.scrollHeight);
    siguiendoRef.current = estaCercaDelFondo(sc, UMBRAL_REENGANCHE_PX);
    setLejosDelFondo(!estaCercaDelFondo(sc, UMBRAL_CERCA_PX));
  }, [scrollRef, clave, activo]);

  // Scroll: guardar la posición, decidir el botón, y reengancharse al llegar al fondo. Un
  // gesto hacia arriba (rueda / dedo) corta el seguimiento aunque siga "cerca".
  useEffect(() => {
    const sc = scrollRef.current;
    if (!sc || !activo) return;
    let raf = 0;
    const alScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        guardarPosicion(clave, sc.scrollTop);
        if (estaCercaDelFondo(sc, UMBRAL_REENGANCHE_PX)) siguiendoRef.current = true;
        setLejosDelFondo(!estaCercaDelFondo(sc, UMBRAL_CERCA_PX));
      });
    };
    const alRueda = (ev: WheelEvent) => {
      if (gestoHaciaArriba({ deltaY: ev.deltaY })) siguiendoRef.current = false;
    };
    let toqueDesdeY: number | null = null;
    const alTocar = (ev: TouchEvent) => {
      toqueDesdeY = ev.touches[0]?.clientY ?? null;
    };
    const alMoverDedo = (ev: TouchEvent) => {
      const y = ev.touches[0]?.clientY;
      if (toqueDesdeY === null || y === undefined) return;
      if (gestoHaciaArriba({ toqueDesdeY, toqueHastaY: y })) siguiendoRef.current = false;
    };
    sc.addEventListener('scroll', alScroll, { passive: true });
    sc.addEventListener('wheel', alRueda, { passive: true });
    sc.addEventListener('touchstart', alTocar, { passive: true });
    sc.addEventListener('touchmove', alMoverDedo, { passive: true });
    return () => {
      sc.removeEventListener('scroll', alScroll);
      sc.removeEventListener('wheel', alRueda);
      sc.removeEventListener('touchstart', alTocar);
      sc.removeEventListener('touchmove', alMoverDedo);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [scrollRef, clave, activo]);

  // Seguir el crecimiento: la columna de mensajes es el primer hijo del scroller.
  useEffect(() => {
    const sc = scrollRef.current;
    const columna = sc?.firstElementChild;
    if (!sc || !columna || !activo || typeof ResizeObserver === 'undefined') return;
    const observador = new ResizeObserver(() => {
      if (siguiendoRef.current) sc.scrollTop = sc.scrollHeight;
    });
    observador.observe(columna);
    return () => observador.disconnect();
  }, [scrollRef, activo]);

  const irAlFondo = useCallback(() => {
    const sc = scrollRef.current;
    if (!sc) return;
    siguiendoRef.current = true;
    sc.scrollTo({ top: sc.scrollHeight, behavior: 'smooth' });
  }, [scrollRef]);

  const alEnviar = useCallback(() => {
    siguiendoRef.current = true;
    // El mensaje nuevo entra en el próximo commit: se baja recién ahí.
    requestAnimationFrame(irAlFondo);
  }, [irAlFondo]);

  return { lejosDelFondo, irAlFondo, alEnviar };
}
